import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BoxService } from '@app/services/boxes/box.service';
import {
  BoxType,
  DEFAULT_DISTANCE_UNIT,
  DEFAULT_WEIGHT_UNIT,
  DistanceUnitEnum,
  InputType,
} from '@pdqdev/pdq-shared/dist/entities';
import { IBoxRules, IBoxSettingsData } from '@pdqdev/pdq-shared/dist/interfaces';
import { validateBoxRulesData } from '@pdqdev/pdq-shared/dist/utils';
import { CanDeactivateEditPackageGuard } from '../edit-package-guard/deactivate-edit-package-gurad';
import { SettingsEditPackageService } from '../edit-package.service';
import { ShopService } from '@app/services/shops/shop.service';

@Component({
  selector: 'app-settings-edit-custom-package',
  templateUrl: './edit-custom-package.component.html',
  styleUrls: ['./edit-custom-package.component.scss', '../../../settings.scss'],
  standalone: false,
})
export class SettingsEditCustomPackageComponent implements OnInit, OnChanges {
  @Input() boxData: IBoxSettingsData;
  @Input() boxesData: IBoxSettingsData[];
  @Input() unavailableKeyBinds = [];
  customTypes: { text: string; value: BoxType }[] = [
    { text: 'Box', value: BoxType.BOX },
    { text: 'Envelope', value: BoxType.ENVELOPE },
    { text: 'Poly mailer / Satchel', value: BoxType.SOFT },
  ];
  distanceList;
  conditionOptions;
  inputType = InputType;
  deliveryOptions = [];
  skuData;
  barcodeData;
  validateBoxRulesDataError;
  defaultCustomType = BoxType.BOX;
  isFormInit: boolean;
  isLoading = false;

  defaultWeightUnits;
  supportsPaccurate = false;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private boxService: BoxService,
    private router: Router,
    public settingsEditPackageService: SettingsEditPackageService,
    private canDeactivateEditPackageGuard: CanDeactivateEditPackageGuard,
    private shopService: ShopService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.boxData && changes.boxesData) {
      this.initEditPackages();
    }
  }

  ngOnInit(): void {
    this.initLists();
    this.settingsEditPackageService.formGroup = null;
    this.settingsEditPackageService.showFormErrors = false;
    this.initEditPackages();
  }

  initLists() {
    this.distanceList = Object.values(DistanceUnitEnum)
      .filter((unit) => [DistanceUnitEnum.M, DistanceUnitEnum.FT, DistanceUnitEnum.YD].indexOf(unit) === -1)
      .map((unit) => {
        return { label: unit, value: unit };
      });
  }

  initEditPackages() {
    const boxData = this.boxData;
    const shop = this.shopService.currentShopValue;
    this.defaultWeightUnits = shop?.metadata?.weightUnits ?? DEFAULT_WEIGHT_UNIT;
    this.supportsPaccurate = shop?.metadata?.paccurate?.enabled;
    this.settingsEditPackageService.formGroup = this.formBuilder.group({
      name: [boxData ? boxData.name : '', Validators.required],
      type: [boxData && boxData.type !== BoxType.FLAT_RATE ? boxData.type : BoxType.BOX, Validators.required],
      length: [boxData ? boxData.length : 0, [Validators.required, Validators.min(0.001)]],
      width: [boxData ? boxData.width : 0, [Validators.required, Validators.min(0.001)]],
      height: [boxData ? boxData.height : 0, [Validators.required, Validators.min(0.001)]],
      distanceUnit: [boxData ? boxData.distanceUnit : DEFAULT_DISTANCE_UNIT],
      maxWeight: [boxData?.maxWeight ? boxData.maxWeight : 0],
      maxWeightUnit: [boxData?.maxWeightUnit ? boxData.maxWeightUnit : this.defaultWeightUnits],
      isUseMaximalWeight: [boxData ? boxData.isUseMaximalWeight : false],
      additionalWeight: [boxData?.additionalWeight ? boxData.additionalWeight : 0],
      additionalWeightUnit: [boxData?.additionalWeightUnit ? boxData.additionalWeightUnit : this.defaultWeightUnits],
      condition: [boxData ? boxData.rules?.operator : 'all'],
      innerDimensions: this.formBuilder.group({
        innerLength: [boxData?.innerDimensions?.innerLength || 0],
        innerWidth: [boxData?.innerDimensions?.innerWidth || 0],
        innerHeight: [boxData?.innerDimensions?.innerHeight || 0],
      }),
      keybind: [boxData?.keybind || ''],
      rulesArray: this.formBuilder.array([]),
    });

    if (this.supportsPaccurate) {
      this.settingsEditPackageService.formGroup.addControl(
        'maxWeightCapacity',
        this.formBuilder.control(boxData?.maxWeightCapacity ? boxData.maxWeightCapacity : 0, [
          Validators.required,
          Validators.min(0.001),
        ]),
      );
      this.settingsEditPackageService.formGroup.addControl(
        'maxWeightCapacityUnit',
        this.formBuilder.control(
          boxData?.maxWeightCapacityUnit ? boxData.maxWeightCapacityUnit : this.defaultWeightUnits,
        ),
      );
    }
    const innerDimensionsGroup = this.settingsEditPackageService.formGroup.get('innerDimensions');

    innerDimensionsGroup?.valueChanges.subscribe((value) => {
      const transformedValue = {
        innerLength: +value.innerLength,
        innerWidth: +value.innerWidth,
        innerHeight: +value.innerHeight,
      };
      innerDimensionsGroup.setValue(transformedValue, { emitEvent: false });
    });

    this.settingsEditPackageService.formGroup.get('isUseMaximalWeight').valueChanges.subscribe((data) => {
      if (data) {
        this.settingsEditPackageService.formGroup.controls.maxWeight.setValidators([
          Validators.required,
          Validators.min(0.001),
        ]);
      } else {
        this.settingsEditPackageService.formGroup.controls.maxWeight.clearValidators();
      }
      this.settingsEditPackageService.formGroup.controls.maxWeight.updateValueAndValidity();
    });

    this.settingsEditPackageService.resetConditions.next(true);
    this.isFormInit = true;
    this.settingsEditPackageService.formGroup.markAsPristine();
  }

  resetForm() {
    this.settingsEditPackageService.showFormErrors = false;
    this.settingsEditPackageService.formGroup = null;
    this.initEditPackages();
  }

  onSubmit() {
    this.isLoading = true;
    this.validateBoxRulesDataError = null;
    if (this.settingsEditPackageService.formGroup.valid) {
      const rules = this.settingsEditPackageService.getRulesData();
      try {
        validateBoxRulesData(rules);
        if (this.boxData?.id) {
          this.boxService
            .updateBox({
              ...this.settingsEditPackageService.formGroup.value,
              rules,
              priority: this.boxData.priority,
              isActive: this.boxData.isActive,
              id: this.boxData.id,
            })
            .subscribe(() => {
              this.shopService.refreshBoxes();
              this.packageSaved();
            });
        } else {
          if (this.boxesData) {
            this.createBox(rules);
          } else {
            this.boxService.getBoxes({ excludeCustomBoxes: true }).subscribe((data) => {
              this.boxesData = data;
              this.createBox(rules);
            });
          }
        }
      } catch (e) {
        this.isLoading = false;
        this.validateBoxRulesDataError = `Condition error: ${e.message}`;
      }
    } else {
      this.isLoading = false;
      this.settingsEditPackageService.showFormErrors = true;
    }
  }

  createBox(rules: IBoxRules) {
    const maxPriority = this.boxesData?.length
      ? Math.max.apply(
          Math,
          this.boxesData.map(function (box) {
            return box.priority;
          }),
        )
      : 0;
    this.boxService
      .createBox({
        ...this.settingsEditPackageService.formGroup.value,
        rules,
        priority: maxPriority ? maxPriority + 1 : 1,
        isActive: true,
      })
      .subscribe(() => {
        this.shopService.refreshBoxes();
        this.packageSaved();
      });
  }

  packageSaved() {
    this.isLoading = false;
    this.router.navigate(['./settings/packages']);
    this.canDeactivateEditPackageGuard.submitted = true;
  }

  getUnitsError(displayRequiredSign = true) {
    const controls = [
      { control: 'length', name: 'Length' },
      { control: 'width', name: 'Width' },
      { control: 'height', name: 'Height' },
    ];
    return this.settingsEditPackageService.getErrorByControls(
      this.settingsEditPackageService.formGroup,
      controls,
      displayRequiredSign,
    );
  }

  getMaxWeightError(displayRequiredSign = true) {
    return this.settingsEditPackageService.getErrorByControls(
      this.settingsEditPackageService.formGroup,
      [{ control: 'maxWeight', name: 'Max weight' }],
      displayRequiredSign,
    );
  }

  getMaxWeightCapacityError(displayRequiredSign = true) {
    if (this.supportsPaccurate) {
      return this.settingsEditPackageService.getErrorByControls(
        this.settingsEditPackageService.formGroup,
        [{ control: 'maxWeightCapacity', name: 'Max package weight' }],
        displayRequiredSign,
      );
    }
  }

  getBannerErrors() {
    const errors = [];
    if (!this.settingsEditPackageService.formGroup.get('name').valid) {
      errors.push('Package name is required');
    }
    const unitError = this.getUnitsError(false);
    if (unitError) {
      errors.push(unitError);
    }
    const maxWeightError = this.getMaxWeightError(false);
    if (maxWeightError) {
      errors.push(maxWeightError);
    }
    const maxWeightCapacityError = this.getMaxWeightCapacityError(false);
    if (maxWeightCapacityError) {
      errors.push(maxWeightCapacityError);
    }

    const rulesArrayForm = <UntypedFormArray>this.settingsEditPackageService.formGroup.controls.rulesArray;

    this.settingsEditPackageService.formGroup.value.rulesArray.forEach((rules, rulesIndex) => {
      rules.forEach((rule, ruleIndex) => {
        const group = <UntypedFormGroup>(rulesArrayForm.controls[rulesIndex] as UntypedFormArray).controls[ruleIndex];
        const conditionError = this.settingsEditPackageService.getConditionError(group, ruleIndex, false);
        if (conditionError) {
          errors.push(`Condition group ${rulesIndex + 1}, condition ${ruleIndex + 1}: ${conditionError}`);
        }
      });
    });
    if (this.validateBoxRulesDataError) {
      errors.push(this.validateBoxRulesDataError);
    }
    return errors;
  }
}
